import { Icon } from '../common/Icon';

export interface IconButtonProps {
  icon: string;
  /** Accessible label, also used as `title` for hover tooltip. */
  label: string;
  onClick?: (e: React.MouseEvent) => void;
  size?: 'sm' | 'md';
  /** Visual variant. `ghost` (default) is transparent with hover wash;
   * `filled` has a soft gray background even at rest. */
  variant?: 'ghost' | 'filled';
  disabled?: boolean;
}

/**
 * Square icon-only button — used in `<PageHeader>` actions, modal
 * close buttons, row trailing actions, toolbars. The label is
 * required for accessibility.
 *
 * @example
 * <IconButton icon="group_add" label="New conversation" onClick={openCreate} />
 */
export function IconButton({
  icon,
  label,
  onClick,
  size = 'md',
  variant = 'ghost',
  disabled,
}: IconButtonProps) {
  const sizeClass = size === 'sm' ? 'w-8 h-8' : 'w-9 h-9';
  const iconSize = size === 'sm' ? 16 : 18;
  const variantClass =
    variant === 'filled'
      ? 'bg-gray-100 dark:bg-white/[0.06] hover:bg-gray-200 dark:hover:bg-white/[0.10]'
      : 'hover:bg-gray-100 dark:hover:bg-white/[0.06]';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`
        flex-shrink-0 rounded-full flex items-center justify-center
        text-gray-600 dark:text-gray-300
        ${sizeClass} ${variantClass}
        disabled:opacity-40 disabled:pointer-events-none
        transition-colors
      `}
    >
      <Icon name={icon} size={iconSize} />
    </button>
  );
}
